import { styled } from '@linaria/react';
import EuroIcon from '@mui/icons-material/Euro';
import FlagIcon from '@mui/icons-material/Flag';
import StraightenIcon from '@mui/icons-material/Straighten';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Button, Popover } from '@mui/material';
import type { ElementType, FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBudget, formatKilometers } from '../../../core/mapV2/format';
import { activeFilterCountQuery, kpisQuery } from '../../../core/mapV2/queries';
import { filtersReset } from '../../../core/mapV2/state';
import theme from '../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../types';
import { FiltersPanel } from './FiltersPanel';

const Bar = styled.header`
    flex: 0 0 72px;
    height: 72px;
    display: flex;
    align-items: center;
    padding: 0 16px;
    box-sizing: border-box;
    background-color: ${theme.color.white.main};
    border-bottom: 1px solid ${theme.color.grey.medium};
`;

const Kpi = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 20px;
    border-left: 1px solid ${theme.color.grey.medium};

    &:first-child {
        padding-left: 0;
        border-left: 0;
    }
`;

const IconCircle = styled.span`
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: ${theme.color.blue.tint3};
    color: ${theme.color.blue.primary};
`;

const Texts = styled.div`
    display: flex;
    flex-direction: column;
`;

const Value = styled.span`
    font-size: 19px;
    font-weight: 600;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: ${theme.color.black.main};
`;

const Label = styled.span`
    font-size: 11.5px;
    color: ${theme.color.grey.light};
`;

const Spacer = styled.div`
    flex: 1;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

type KpiProps = { icon: ElementType; value: string; label: string };

const KpiCell: FC<KpiProps> = ({ icon: Icon, value, label }) => (
    <Kpi title={label}>
        <IconCircle>
            <Icon fontSize="small" />
        </IconCircle>
        <Texts>
            <Value>{value}</Value>
            <Label>{label}</Label>
        </Texts>
    </Kpi>
);

// Four indicators for the perimeter being looked at, and the filters: a button with the number of active
// filters opens the criteria, another one resets them. The figures come from the state and project layers,
// so they show a dash until those phases.
export const KpiBar: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const kpis = useAppSelector(kpisQuery);
    const activeFilters = useAppSelector(activeFilterCountQuery);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    return (
        <Bar data-testid="map-v2-kpi-bar">
            <KpiCell
                icon={StraightenIcon}
                value={formatKilometers(kpis?.analyzedMeters ?? null)}
                label={t('mapV2.kpi.analyzed')}
            />
            <KpiCell
                icon={FlagIcon}
                value={formatKilometers(kpis?.priorityMeters ?? null)}
                label={t('mapV2.kpi.priority')}
            />
            <KpiCell
                icon={WarningAmberIcon}
                value={formatKilometers(kpis?.criticalMeters ?? null)}
                label={t('mapV2.kpi.critical')}
            />
            <KpiCell icon={EuroIcon} value={formatBudget(kpis?.budgetEuros ?? null)} label={t('mapV2.kpi.budget')} />
            <Spacer />
            <Actions>
                <Button
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: 'none' }}
                    aria-haspopup="dialog"
                    onClick={(event) => setAnchor(event.currentTarget)}
                >
                    {t('mapV2.kpi.activeFilters', { count: activeFilters })}
                </Button>
                <Button
                    size="small"
                    disabled={activeFilters === 0}
                    sx={{ textTransform: 'none' }}
                    onClick={() => dispatch(filtersReset())}
                >
                    {t('mapV2.kpi.reset')}
                </Button>
            </Actions>
            <Popover
                open={anchor !== null}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <FiltersPanel />
            </Popover>
        </Bar>
    );
};
